import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  Search,
  Filter,
  ArrowLeft,
  Video,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Play,
  Eye,
  ExternalLink,
  Trash2
} from 'lucide-react'

interface VideoWithUser {
  id: string
  title: string
  description: string | null
  video_url: string
  thumbnail_url: string | null
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  user: {
    id: string
    full_name: string | null
    email: string | null
    avatar_url: string | null
  } | null
}

export function AdminVideosPage() {
  const navigate = useNavigate()
  const [videos, setVideos] = useState<VideoWithUser[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedVideo, setSelectedVideo] = useState<VideoWithUser | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [rejectionReason, setRejectionReason] = useState('')
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [videoToDelete, setVideoToDelete] = useState<string | null>(null)

  useEffect(() => {
    fetchVideos()
  }, [filter, searchQuery])

  const fetchVideos = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('videos')
        .select(`
          *,
          user:profiles!user_id (
            id,
            full_name,
            email,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false })

      if (filter !== 'all') {
        query = query.eq('status', filter)
      }

      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
      }

      const { data, error } = await query

      if (error) throw error
      setVideos(data || [])
    } catch (err) {
      console.error('Error fetching videos:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (videoId: string, action: 'approve' | 'reject') => {
    try {
      setLoading(true)
      
      // Update video status
      const { error: updateError } = await supabase
        .from('videos')
        .update({ 
          status: action === 'approve' ? 'approved' : 'rejected',
          ...(action === 'reject' && { rejection_reason: rejectionReason })
        })
        .eq('id', videoId)

      if (updateError) throw updateError

      // Get video details for notification
      const { data: videoData, error: videoError } = await supabase
        .from('videos')
        .select('title, user_id')
        .eq('id', videoId)
        .single()

      if (videoError) throw videoError

      // Create notification for user
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([{
          user_id: videoData.user_id,
          title: `Video ${action === 'approve' ? 'Approved' : 'Rejected'}`,
          content: action === 'approve' 
            ? `Your video "${videoData.title}" has been approved and is now public.`
            : `Your video "${videoData.title}" has been rejected. ${rejectionReason ? `Reason: ${rejectionReason}` : ''}`,
          type: 'system',
          metadata: {
            video_id: videoId,
            action
          }
        }])

      if (notificationError) throw notificationError

      setToastMessage(`Video ${action === 'approve' ? 'approved' : 'rejected'} successfully`)
      setToastType('success')
      setShowToast(true)
      setShowDetails(false)
      setShowRejectionModal(false)
      setRejectionReason('')
      
      // Refresh videos list
      fetchVideos()
    } catch (err) {
      console.error(`Error ${action}ing video:`, err)
      setToastMessage(`Error ${action}ing video`)
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteVideo = async () => {
    if (!videoToDelete) return

    try {
      setLoading(true)
      
      // Get video details for notification
      const { data: videoData, error: videoError } = await supabase
        .from('videos')
        .select('title, user_id')
        .eq('id', videoToDelete)
        .single()

      if (videoError) throw videoError

      // Delete the video
      const { error: deleteError } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoToDelete)

      if (deleteError) throw deleteError

      // Create notification for user
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([{
          user_id: videoData.user_id,
          title: "Video Deleted",
          content: `Your video "${videoData.title}" has been deleted by an administrator.`,
          type: 'system',
          metadata: {
            action: 'delete'
          }
        }])

      if (notificationError) throw notificationError

      setToastMessage('Video deleted successfully')
      setToastType('success')
      setShowToast(true)
      
      // Refresh videos list
      fetchVideos()
    } catch (err) {
      console.error('Error deleting video:', err)
      setToastMessage('Error deleting video')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
      setShowDeleteConfirm(false)
      setVideoToDelete(null)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        )
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Video Details Modal */}
      {showDetails && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Video Details
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Video Preview */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Video Preview</h4>
                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                    {selectedVideo.video_url.includes('youtube.com') || selectedVideo.video_url.includes('youtu.be') ? (
                      <iframe
                        src={selectedVideo.video_url.replace('watch?v=', 'embed/')}
                        title={selectedVideo.title}
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <a 
                          href={selectedVideo.video_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center text-white bg-primary px-4 py-2 rounded-lg"
                        >
                          <ExternalLink className="h-5 w-5 mr-2" />
                          Open Video
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Video Information</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedVideo.title}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedVideo.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      {getStatusBadge(selectedVideo.status)}
                    </div>
                  </div>
                  {selectedVideo.description && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-700">{selectedVideo.description}</p>
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Uploaded By</h4>
                  <div className="mt-2 flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      {selectedVideo.user?.avatar_url ? (
                        <img
                          src={selectedVideo.user.avatar_url}
                          alt={selectedVideo.user.full_name || ''}
                          className="h-10 w-10 rounded-full"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        {selectedVideo.user?.full_name || 'Anonymous'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedVideo.user?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-6 border-t flex justify-between">
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => {
                      setVideoToDelete(selectedVideo.id)
                      setShowDetails(false)
                      setShowDeleteConfirm(true)
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Video
                  </Button>
                  
                  <div className="flex space-x-3">
                    <Button
                      variant="secondary"
                      onClick={() => setShowDetails(false)}
                    >
                      Close
                    </Button>
                    {selectedVideo.status === 'pending' && (
                      <>
                        <Button
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setShowRejectionModal(true)
                            setShowDetails(false)
                          }}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => handleAction(selectedVideo.id, 'approve')}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectionModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Reject Video
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Please provide a reason for rejecting this video. This will be sent to the user.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Reason for rejection..."
            />
            <div className="mt-6 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRejectionModal(false)
                  setShowDetails(true)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => handleAction(selectedVideo.id, 'reject')}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">
                Confirm Deletion
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this video? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setVideoToDelete(null)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={handleDeleteVideo}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Video Management
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Review and manage user-submitted videos
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search videos..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as typeof filter)}
                  className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">All videos</option>
                  <option value="pending">Pending review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : videos.length === 0 ? (
              <div className="text-center py-12">
                <Video className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No videos</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No videos match your current filters
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Video
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploader
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {videos.map((video) => (
                      <tr key={video.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                              {video.thumbnail_url ? (
                                <img
                                  src={video.thumbnail_url}
                                  alt={video.title}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                  <Video className="h-5 w-5 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {video.title}
                              </div>
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {video.description?.substring(0, 50) || 'No description'}
                                {video.description && video.description.length > 50 ? '...' : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 flex-shrink-0">
                              {video.user?.avatar_url ? (
                                <img
                                  src={video.user.avatar_url}
                                  alt={video.user.full_name || ''}
                                  className="h-8 w-8 rounded-full"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User className="h-4 w-4 text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {video.user?.full_name || 'Anonymous'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {video.user?.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(video.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(video.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedVideo(video)
                                setShowDetails(true)
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Button>
                            {video.status === 'pending' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => {
                                    setSelectedVideo(video)
                                    setShowRejectionModal(true)
                                  }}
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Reject
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleAction(video.id, 'approve')}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Approve
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                setVideoToDelete(video.id)
                                setShowDeleteConfirm(true)
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}